import { Injectable, computed, signal } from '@angular/core';
import {
  IAmountSelection,
  IEmployerSelection,
  IRegimeFundSelection,
  ISubscriptionDraft,
} from '../models/subscription.model';

interface ISubscriptionState {
  amount: IAmountSelection | null;
  regimeFund: IRegimeFundSelection | null;
  employer: IEmployerSelection | null;
  editMode: boolean;
  hasEmployer: boolean;
  lastStepUrl: string | null;
}

const INITIAL_STATE: ISubscriptionState = {
  amount: null,
  regimeFund: null,
  employer: null,
  editMode: false,
  hasEmployer: true,
  lastStepUrl: null,
};

@Injectable({ providedIn: 'root' })
export class SubscriptionStore {
  private readonly state = signal<ISubscriptionState>(INITIAL_STATE);

  readonly amount = computed(() => this.state().amount);
  readonly regimeFund = computed(() => this.state().regimeFund);
  readonly employer = computed(() => this.state().employer);
  readonly editMode = computed(() => this.state().editMode);
  readonly hasEmployer = computed(() => this.state().hasEmployer);
  readonly lastStepUrl = computed(() => this.state().lastStepUrl);

  readonly draft = computed<ISubscriptionDraft | null>(() => {
    const { amount, regimeFund, employer } = this.state();

    return amount && regimeFund && employer
      ? { amount, regimeFund, employer }
      : null;
  });

  setAmount(amount: IAmountSelection): void {
    this.patch({ amount });
  }

  setRegimeFund(regimeFund: IRegimeFundSelection): void {
    this.patch({ regimeFund });
  }

  setEmployer(employer: IEmployerSelection): void {
    this.patch({ employer });
  }

  setEditMode(editMode: boolean): void {
    this.patch({ editMode });
  }

  setHasEmployer(hasEmployer: boolean): void {
    this.patch({ hasEmployer });
  }

  setLastStepUrl(lastStepUrl: string): void {
    this.patch({ lastStepUrl });
  }

  reset(): void {
    this.state.set({ ...INITIAL_STATE, hasEmployer: this.state().hasEmployer });
  }

  private patch(changes: Partial<ISubscriptionState>): void {
    this.state.update((state) => ({ ...state, ...changes }));
  }
}
